import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Video, Clock, Loader2, Radio } from 'lucide-react';
import { formatDate, formatTime, formatTimeLocal, formatLocalDate, getLocalDayMonth, getTimezoneAbbr, isIST, getCourseTypeLabel } from '../lib/utils';

export default function PublicJoin() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoJoining, setAutoJoining] = useState(false);
  const hasAutoJoinedRef = useRef(false);

  const fetchData = (onSuccess) => {
    api.get(`/api/public/join/${slug}`)
      .then((res) => {
        setData(res.data);
        onSuccess?.(res.data);
      })
      .catch((err) => setError(err.response?.data?.detail || 'Invalid link'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData((fresh) => {
      // V14.5 — only auto-join if there's exactly ONE live session.
      // If 2+ live OR live+starting_soon in parallel → show chooser.
      const liveCount = (fresh?.sessions || []).filter(
        s => s?.start_state === 'live' && s?.can_join && s?.zoom_join_url
      ).length;
      const liveOrSoonCount = (fresh?.sessions || []).filter(
        s => s?.start_state === 'live' || s?.start_state === 'starting_soon'
      ).length;
      if (
        liveCount === 1
        && liveOrSoonCount <= 1
        && fresh?.session?.can_join
        && fresh?.session?.zoom_join_url
        && !hasAutoJoinedRef.current
      ) {
        hasAutoJoinedRef.current = true;
        setAutoJoining(true);
        setTimeout(() => {
          window.open(fresh.session.zoom_join_url, '_blank');
          setAutoJoining(false);
        }, 1500);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Poll every 15 s ALWAYS — the public page must stay in sync with session.status
  // transitions (upcoming → live → completed) so the messaging and join button
  // reflect reality without the learner needing to refresh manually.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData((fresh) => {
        const liveCount = (fresh?.sessions || []).filter(
          s => s?.start_state === 'live' && s?.can_join && s?.zoom_join_url
        ).length;
        const liveOrSoonCount = (fresh?.sessions || []).filter(
          s => s?.start_state === 'live' || s?.start_state === 'starting_soon'
        ).length;
        if (
          liveCount === 1
          && liveOrSoonCount <= 1
          && fresh?.session?.can_join
          && fresh?.session?.zoom_join_url
          && !hasAutoJoinedRef.current
        ) {
          hasAutoJoinedRef.current = true;
          setTimeout(() => window.open(fresh.session.zoom_join_url, '_blank'), 800);
        }
      });
    }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="h-12 w-12 rounded-2xl overflow-hidden bg-white flex items-center justify-center mb-4 border">
        <img src="/logo.png" alt="Soul Sanskar" className="h-16 w-16 object-contain scale-150" />
      </div>
      <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
      <p className="text-sm text-muted-foreground mt-3">Loading session...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
      <div className="h-12 w-12 rounded-2xl overflow-hidden bg-white flex items-center justify-center mb-4 border">
        <img src="/logo.png" alt="Soul Sanskar" className="h-16 w-16 object-contain scale-150" />
      </div>
      <p className="text-destructive font-medium">Link Not Found</p>
      <p className="text-sm text-muted-foreground mt-1">{typeof error === 'string' ? error : 'This link may be invalid or disabled.'}</p>
    </div>
  );

  const session = data?.session;
  const parallelSessions = data?.sessions || (session ? [session] : []);
  // Session lifecycle state drives the UI — derived from backend's current truth
  const status = session?.status;  // 'upcoming' | 'live' | 'completed' | 'missed'
  const isLive = status === 'live' && session?.can_join && session?.zoom_join_url;
  const isUpcoming = status === 'upcoming';
  const isEnded = status === 'completed' || status === 'missed';
  // V14.5 / B4 — when 2+ sessions are live OR live+about-to-start, show chooser
  const liveOrSoon = parallelSessions.filter(
    s => s?.start_state === 'live' || s?.start_state === 'starting_soon'
  );
  const hasMultipleLiveOrSoon = liveOrSoon.length >= 2;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="h-14 w-14 mx-auto rounded-2xl overflow-hidden bg-white flex items-center justify-center border mb-2">
            <img src="/logo.png" alt="Soul Sanskar" className="h-20 w-20 object-contain scale-150" />
          </div>
          <h1 className="font-heading text-xl font-semibold">{data?.batch_name}</h1>
          <Badge variant="secondary" className="mt-1">{getCourseTypeLabel(data?.course_type)}</Badge>
        </div>

        {hasMultipleLiveOrSoon ? (
          <Card className="rounded-2xl" data-testid="parallel-sessions-card">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                </span>
                <p className="text-sm font-semibold text-emerald-700">
                  {liveOrSoon.filter(s => s.start_state === 'live').length} live
                  {liveOrSoon.filter(s => s.start_state === 'starting_soon').length > 0 &&
                    <> · {liveOrSoon.filter(s => s.start_state === 'starting_soon').length} starting soon</>
                  }
                </p>
              </div>
              <p className="text-xs text-muted-foreground text-center mb-4">
                Pick the one you booked — multiple classes are happening in this batch.
              </p>
              <div className="space-y-3">
                {liveOrSoon.map(s => {
                  const live = s.start_state === 'live' && s.can_join && s.zoom_join_url;
                  const soon = s.start_state === 'starting_soon';
                  const minsLeft = Math.max(0, Math.round(s.minutes_until_start || 0));
                  return (
                    <Card key={s.id} className={`rounded-xl border-2 transition-colors ${live ? 'hover:border-primary/50' : 'opacity-70'}`} data-testid={`parallel-session-${s.id}`}>
                      <CardContent className="pt-3 pb-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate">{s.title || 'Live Session'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {live ? (
                              <Badge className="bg-rose-600 text-white border-0 text-[9px]"><Radio className="h-2.5 w-2.5 mr-1" />Live</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[9px] bg-amber-50 border-amber-200 text-amber-800"><Clock className="h-2.5 w-2.5 mr-1" />Starts in {minsLeft}m</Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {formatTimeLocal(s.scheduled_time, s.scheduled_date)} {getTimezoneAbbr()}
                            </span>
                          </div>
                        </div>
                        {live ? (
                          <Button size="sm" onClick={() => window.open(s.zoom_join_url, '_blank')} data-testid={`parallel-join-${s.id}`}>
                            <Video className="h-3.5 w-3.5 mr-1" />Join
                          </Button>
                        ) : (
                          <Button size="sm" disabled className="opacity-50" data-testid={`parallel-pending-${s.id}`}>
                            <Clock className="h-3.5 w-3.5 mr-1" />Waiting
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-3">Auto-refreshes every 15s</p>
            </CardContent>
          </Card>
        ) : session ? (
          <Card className="rounded-2xl" data-testid="public-session-card">
            <CardContent className="pt-5 pb-5">
              {/* Session info row */}
              <div className="flex items-center gap-3 mb-4">
                {(() => {
                  // V14.3 — TZ-aware calendar badge: a 6 AM IST class shows as
                  // 5:30 PM PST the previous day for a PST learner. Both the
                  // date badge and the date text below must respect the
                  // learner's local TZ so the public link never lies.
                  const dm = getLocalDayMonth(session.scheduled_date, session.scheduled_time);
                  return (
                    <div className="text-center min-w-[44px] bg-primary/10 rounded-xl px-2 py-1" data-testid="session-date-badge">
                      <p className="text-[9px] text-primary uppercase">{dm.month}</p>
                      <p className="text-lg font-semibold text-primary leading-tight">{dm.day}</p>
                    </div>
                  );
                })()}
                <div className="flex-1">
                  <p className="text-sm font-medium" data-testid="session-local-date">
                    {formatLocalDate(session.scheduled_date, session.scheduled_time)}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="session-local-time">
                    {formatTimeLocal(session.scheduled_time, session.scheduled_date)} {getTimezoneAbbr()} · {session.duration_minutes} min
                  </p>
                  {!isIST() && (
                    <p className="text-[10px] text-muted-foreground/80" data-testid="session-ist-fallback">
                      ({formatTime(session.scheduled_time)} IST)
                    </p>
                  )}
                </div>
                {isLive && (
                  <Badge className="bg-rose-600 text-white border-0 gap-1" data-testid="live-badge">
                    <Radio className="h-3 w-3" />Live
                  </Badge>
                )}
              </div>

              {/* --- STATE MACHINE --- */}
              {autoJoining ? (
                <div className="text-center py-6" data-testid="state-auto-joining">
                  <div className="relative mx-auto w-16 h-16 mb-3">
                    <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
                    <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                    <Video className="absolute inset-0 m-auto h-6 w-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium">Connecting to class...</p>
                  <p className="text-xs text-muted-foreground mt-1">Opening Zoom in a new tab</p>
                </div>
              ) : isLive ? (
                <div className="text-center py-2" data-testid="state-live">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                    </span>
                    <p className="text-sm font-semibold text-emerald-700">Class is live now</p>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">
                    Click below to join. You may be placed briefly in the Zoom waiting room while the instructor admits you.
                  </p>
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => window.open(session.zoom_join_url, '_blank')}
                    data-testid="join-now-btn"
                  >
                    <Video className="h-4 w-4 mr-2" />Join Class Now
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center mt-3">
                    Already in Zoom? <button className="underline" onClick={() => window.open(session.zoom_join_url, '_blank')}>Rejoin</button>
                  </p>
                </div>
              ) : isUpcoming ? (
                <div className="text-center py-4" data-testid="state-upcoming">
                  <div className="relative mx-auto w-16 h-16 mb-3">
                    <div className="absolute inset-0 rounded-full bg-amber-50 flex items-center justify-center">
                      <div className="h-3 w-3 rounded-full bg-amber-400 animate-pulse" />
                    </div>
                  </div>
                  <p className="text-sm font-medium text-amber-800">Waiting for instructor to start…</p>
                  <p className="text-xs text-muted-foreground mt-1">The class will begin shortly. This page will refresh automatically when the instructor starts the session.</p>
                </div>
              ) : isEnded ? (
                <div className="text-center py-4" data-testid="state-ended">
                  <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm font-medium">This class has ended</p>
                  <p className="text-xs text-muted-foreground mt-1">Check back later for the next session.</p>
                </div>
              ) : (
                <div className="text-center py-4" data-testid="state-no-session">
                  <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Meeting link will be available when class starts</p>
                </div>
              )}

              <p className="text-[10px] text-muted-foreground text-center mt-3">Joining via public link</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-2xl">
            <CardContent className="pt-5 pb-5 text-center">
              <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{data?.message || 'No upcoming sessions'}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
